import { Disclosure, Transition } from '@headlessui/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useLockBodyScroll } from './useLockBodyScroll';

export default function ProjectModal({ project, onClose, isOpen }) {
	if (!project) return null;

	useLockBodyScroll(true);

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<div
			onClick={handleOverlayClick}
			className={`
        fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70
        transition-opacity duration-300 ease-out
        ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
      `}
		>
			<div className={`bg-gray-900 text-white w-full max-w-7xl max-h-[90vh] p-8 rounded-xl overflow-y-auto custom-scrollbar transform transition-all duration-300 ease-out ${isOpen ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-5 opacity-0 scale-95'}`}>
				<div className="flex flex-col min-h-full">
					{/* Close button */}
					<button
						onClick={onClose}
						className="absolute top-2 right-4 text-3xl text-gray-400 hover:text-white"
					>
						&times;
					</button>

					{/* Title & Subtitle */}
					<h2 className={`text-5xl font-bold ${project.color} mb-2`}>
						{project.title}
					</h2>
					<p className="text-lg mb-4 italic text-gray-300">
						{project.subtitle} — {project.engine}
					</p>

					{/* Videos */}
					<div className="bg-gray-800 p-4 rounded-lg mb-6">
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							{project.videos.map((src, idx) => (
								<video
									key={idx}
									src={src}
									muted
									autoPlay
									loop
									playsInline
									className="w-full rounded object-cover"
								/>
							))}
						</div>
					</div>

					{/* Description */}
					<p className="text-lg mb-4">{project.description}</p>

					{/* Contributions */}
					<h3 className="font-semibold mt-6 mb-2 text-2xl">Here's what I did:</h3>
					<ul className="space-y-2 mb-8">
						{project.contributions.map((item, index) => (
							<li key={index} className="flex items-start gap-3">
								<span className="text-green-400 mt-1 text-xl leading-none">✔</span>
								<p className="text-white">{item}</p>
							</li>
						))}
					</ul>

					{/* Code Examples */}
					{project.codeExamples && project.codeExamples.length > 0 && (
						<div className="mt-10">
							<h3 className="text-2xl font-semibold mb-4">Code Examples</h3>
							<div className="space-y-4">
								{project.codeExamples.map((example, idx) => (
									<Disclosure key={idx}>
										{({ open }) => (
											<div className="border border-gray-700 rounded-lg overflow-hidden">
												<Disclosure.Button className="w-full text-left px-4 py-3 bg-gray-800 hover:bg-gray-700 flex justify-between items-center">
													<span className="text-lg font-medium text-white">{example.title}</span>
													<span className="text-gray-400 text-xl">{open ? '−' : '+'}</span>
												</Disclosure.Button>

												<Transition
													show={open}
													enter="transition-all duration-300 ease-out"
													enterFrom="opacity-0 max-h-0"
													enterTo="opacity-100 max-h-screen"
													leave="transition-all duration-200 ease-in"
													leaveFrom="opacity-100 max-h-screen"
													leaveTo="opacity-0 max-h-0"
												>
													<Disclosure.Panel
														static
														className="bg-gray-900 px-4 py-4 space-y-3 overflow-hidden"
													>
														<p className="text-gray-300">{example.description}</p>

														{example.code && (
															<SyntaxHighlighter
																language="csharp"
																style={vscDarkPlus}
																customStyle={{
																	borderRadius: '0.5rem',
																	fontSize: '0.9rem',
																	background: '#1a1a1a',
																	padding: '1rem',
																}}
															>
																{example.code}
															</SyntaxHighlighter>
														)}
													</Disclosure.Panel>
												</Transition>
											</div>
										)}
									</Disclosure>
								))}
							</div>
						</div>
					)}

					{/* Links + Footer */}
					<div className="mt-8 flex flex-wrap items-center gap-4">
						{project.github && (
							<a
								href={project.github}
								target="_blank"
								rel="noopener noreferrer"
								className="bg-gray-800 text-white px-6 py-3 rounded-md text-lg font-medium hover:bg-gray-700 transition"
							>
								View on GitHub
							</a>
						)}

						{project.steam && (
							<a
								href={project.steam}
								target="_blank"
								rel="noopener noreferrer"
								className="bg-blue-900 text-white px-6 py-3 rounded-md text-lg font-medium hover:bg-blue-700 transition"
							>
								View on Steam
							</a>
						)}

						{project.playLink && (
							<a
								href={project.playLink}
								target="_blank"
								rel="noopener noreferrer"
								className="bg-red-600 text-white px-6 py-3 rounded-md text-lg font-medium hover:bg-red-500 transition"
							>
								Play on Itch.io
							</a>
						)}

						{project.year && (
							<div className="ml-auto text-gray-400 text-2xl font-bold whitespace-nowrap">
								<span className="text-lg px-2">Glumpis Games</span> {project.year}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
